import type { Href } from 'expo-router'
import { Link, useLocalSearchParams, usePathname } from 'expo-router'
import { ScrollView, StyleSheet, View } from 'react-native'
import { Divider, Icon, Text, useTheme } from 'react-native-paper'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { getNavItems } from '@/constants/navigation'
import { useConnectivityMonitor } from '@/hooks/useConnectivityMonitor'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { OrgHeading } from './OrgHeading'

const SIDEBAR_WIDTH = 240

function OfflineOverlay({ isVisible }: { isVisible: boolean }) {
    const theme = useTheme()

    if (!isVisible) return null

    return (
        <View
            style={[styles.offlineOverlay, { backgroundColor: theme.colors.surface }]}
            pointerEvents="box-only"
        />
    )
}

export function Sidebar() {
    const theme = useTheme()
    const pathname = usePathname()
    const insets = useSafeAreaInsets()
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()
    const { role } = useCurrentOrg({ throwIfNotFound: false })
    const { isConnected } = useConnectivityMonitor()
    const isOffline = isConnected === false

    const isActive = (itemPathname: string) => {
        const resolvedPath = itemPathname.replace('[orgSlug]', orgSlug)
        return pathname === resolvedPath || pathname.startsWith(`${resolvedPath}/`)
    }

    return (
        <View
            style={[
                styles.container,
                {
                    backgroundColor: theme.colors.surface,
                    borderRightColor: theme.colors.outlineVariant,
                    paddingTop: insets.top,
                },
            ]}
            testID="desktop-sidebar"
        >
            <OrgHeading orgSlug={orgSlug} />
            <Divider style={styles.divider} />
            <View style={styles.navArea}>
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    {getNavItems(role).map(item => {
                        const active = isActive(item.pathname)
                        const href = {
                            pathname: item.pathname,
                            params: { orgSlug },
                        } as Href

                        return (
                            <Link
                                key={item.pathname}
                                href={href}
                                style={[
                                    styles.navLink,
                                    {
                                        backgroundColor: active
                                            ? theme.colors.primaryContainer
                                            : 'transparent',
                                    },
                                ]}
                                // E2E: gotoScreen() navigates by this testID. Do not remove.
                                testID={`sidebar-${item.testID}`}
                            >
                                <View style={styles.navItem}>
                                    <View
                                        style={[
                                            styles.iconContainer,
                                            { backgroundColor: item.iconBackgroundColor },
                                        ]}
                                    >
                                        <Icon
                                            source={item.iconName}
                                            size={20}
                                            color={item.iconColor}
                                        />
                                    </View>
                                    <Text
                                        variant="bodyMedium"
                                        style={[
                                            styles.navLabel,
                                            {
                                                color: active
                                                    ? theme.colors.onPrimaryContainer
                                                    : theme.colors.onSurface,
                                            },
                                        ]}
                                        numberOfLines={1}
                                    >
                                        {item.title}
                                    </Text>
                                </View>
                            </Link>
                        )
                    })}
                </ScrollView>
                <OfflineOverlay isVisible={isOffline} />
            </View>
        </View>
    )
}

export { SIDEBAR_WIDTH }

const styles = StyleSheet.create({
    container: {
        width: SIDEBAR_WIDTH,
        height: '100%',
        borderRightWidth: 1,
    },
    divider: {
        marginHorizontal: 12,
    },
    navArea: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    offlineOverlay: {
        ...StyleSheet.absoluteFillObject,
        opacity: 0.7,
    },
    scrollContent: {
        paddingVertical: 12,
        paddingHorizontal: 8,
        gap: 4,
    },
    navLink: {
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    navItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconContainer: {
        width: 32,
        height: 32,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    navLabel: {
        flex: 1,
        fontWeight: '500',
    },
})
